import React from 'react';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Legend
} from 'recharts';
import { DevicePhoneMobileIcon, ComputerDesktopIcon } from '@heroicons/react/24/outline';

/**
 * DeviceBrowserCharts - Device type and browser distribution charts
 */
const DeviceBrowserCharts = ({ data, loading }) => {
  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <LoadingCard title="Device Types" />
        <LoadingCard title="Browsers" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <EmptyCard title="Device Types" icon={DevicePhoneMobileIcon} />
        <EmptyCard title="Browsers" icon={ComputerDesktopIcon} />
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <DeviceTypeChart devices={data.devices} />
      <BrowserChart browsers={data.browsers} />
    </div>
  );
};

/**
 * Device type pie chart
 */
const DeviceTypeChart = ({ devices }) => {
  const colors = ['#3b82f6', '#8b5cf6', '#f59e0b', '#10b981', '#ef4444'];

  if (!devices || devices.length === 0) {
    return <EmptyCard title="Device Types" icon={DevicePhoneMobileIcon} />;
  }

  // API returns { name, count, percentage } for each device
  const chartData = devices.slice(0, 5).map((item, index) => ({
    name: item.name || item.device || 'Unknown',
    value: item.count,
    color: colors[index % colors.length]
  }));

  const total = chartData.reduce((sum, item) => sum + item.value, 0);

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <h3 className="text-sm font-medium text-gray-700 mb-4 flex items-center gap-2">
        <DevicePhoneMobileIcon className="w-4 h-4" />
        Device Types
      </h3>
      <div className="h-48">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={chartData}
              cx="50%"
              cy="50%"
              innerRadius={35}
              outerRadius={60}
              paddingAngle={2}
              dataKey="value"
            >
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip
              formatter={(value) => [
                `${value.toLocaleString()} (${((value / total) * 100).toFixed(1)}%)`,
                'Requests'
              ]}
              contentStyle={{
                backgroundColor: 'white',
                border: '1px solid #e5e7eb',
                borderRadius: '6px',
                fontSize: '11px'
              }}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <div className="mt-2 space-y-1">
        {chartData.map((item, index) => (
          <div key={index} className="flex items-center justify-between text-xs">
            <div className="flex items-center gap-2">
              <div
                className="w-2 h-2 rounded-full"
                style={{ backgroundColor: item.color }}
              />
              <span className="text-gray-700">{item.name}</span>
            </div>
            <span className="text-gray-500">
              {((item.value / total) * 100).toFixed(1)}%
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

/**
 * Browser distribution chart
 */
const BrowserChart = ({ browsers }) => {
  const colors = ['#10b981', '#6366f1', '#f59e0b', '#ec4899', '#8b5cf6'];

  if (!browsers || browsers.length === 0) {
    return <EmptyCard title="Browsers" icon={ComputerDesktopIcon} />;
  }

  // API returns { name, count, percentage } for each browser
  const chartData = browsers.slice(0, 5).map((item, index) => ({
    name: item.name || item.browser || 'Unknown',
    value: item.count,
    color: colors[index % colors.length]
  }));

  const total = chartData.reduce((sum, item) => sum + item.value, 0);

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <h3 className="text-sm font-medium text-gray-700 mb-4 flex items-center gap-2">
        <ComputerDesktopIcon className="w-4 h-4" />
        Browsers
      </h3>
      <div className="h-48">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={chartData}
              cx="50%"
              cy="50%"
              innerRadius={35}
              outerRadius={60}
              paddingAngle={2}
              dataKey="value"
            >
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip
              formatter={(value) => [
                `${value.toLocaleString()} (${((value / total) * 100).toFixed(1)}%)`,
                'Requests'
              ]}
              contentStyle={{
                backgroundColor: 'white',
                border: '1px solid #e5e7eb',
                borderRadius: '6px',
                fontSize: '11px'
              }}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <div className="mt-2 space-y-1">
        {chartData.map((item, index) => (
          <div key={index} className="flex items-center justify-between text-xs">
            <div className="flex items-center gap-2">
              <div
                className="w-2 h-2 rounded-full"
                style={{ backgroundColor: item.color }}
              />
              <span className="text-gray-700">{item.name}</span>
            </div>
            <span className="text-gray-500">
              {((item.value / total) * 100).toFixed(1)}%
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

/**
 * Loading card placeholder
 */
const LoadingCard = ({ title }) => (
  <div className="bg-white rounded-lg border border-gray-200 p-4">
    <div className="h-4 bg-gray-200 rounded w-24 mb-4 animate-pulse"></div>
    <div className="h-48 bg-gray-100 rounded animate-pulse"></div>
  </div>
);

/**
 * Empty state card
 */
const EmptyCard = ({ title, icon: Icon }) => (
  <div className="bg-white rounded-lg border border-gray-200 p-4">
    <h3 className="text-sm font-medium text-gray-700 mb-4 flex items-center gap-2">
      <Icon className="w-4 h-4" />
      {title}
    </h3>
    <div className="h-48 flex items-center justify-center text-gray-500 text-sm">
      No data available
    </div>
  </div>
);

export default DeviceBrowserCharts;
